import { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Search, Plus, Edit, Trash2, Tag, UserRound, CalendarDays, Building2, LoaderCircle } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import TenantLists from "../TenantLists";
import useUserStore from "../../../services/userStore";
import { createTags, deleteTag, getAllTags, updateTag } from "./Api/tagdetailsapi";
import { Skeleton } from "../../ui/skeleton";
import { formatDate } from "../formatTimeAgo";
import ConfirmationDialog from "../Endpoints/ConfirmationDialog";
import { myLocalStorage } from "../../StorageHelper";
import EmptyStateCard from "../EmptyStateCard";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import CardPagination from "../CardPagination";
import "./Tags.css";
import "../ManagementDialog.css";
import {
    LIST_STATE_KEYS,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    writePersistedListState,
    useSkipInitialPageReset,
} from "../../../utils/listPaginationState";

interface TagData {
    id: string;
    name: string;
    tenantName: string;
    description?: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
}

export default function TagsDetails() {
    const savedState = readPersistedListState(LIST_STATE_KEYS.tags);
    const { toast } = useToast();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(parsePersistedString(savedState.selectedTenant));
    const [searchQuery, setSearchQuery] = useState(parsePersistedString(savedState.searchQuery));
    const [tagName, setTagName] = useState("");
    const [tagDescription, setTagDescription] = useState("");
    const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedState.currentPage, 1));
    const runTenantChangeWithPageReset = useSkipInitialPageReset(setCurrentPage);
    const { email } = useUserStore((state) => state.user);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [tags, setTags] = useState<TagData[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<TagData>();
    const [isEdit, setIsEdit] = useState<boolean>(false)
    const userData = useUserStore((state) => state.user);
    const storedRole = myLocalStorage.getItem("role");
    const normalizedRole = (userData?.userRole || userData?.role || storedRole || "").toLowerCase();
    const hasElevatedTagPrivileges =
        normalizedRole === "whiteswan-admin" ||
        userData?.email?.startsWith("wsadmin");

    const TAGS_PER_PAGE = 15;

    const fetchTags = async () => {
        try {
            setLoading(true);
            let response = await getAllTags(selectedTenant, currentPage - 1, searchQuery);
            const sorted = [...response.content];
            setTags(sorted);
            setTotalPages(response.totalPages ?? 0);
            setTotalElements(response.totalElements ?? sorted.length);
        } catch (error) {
            console.error("Error fetching tags:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTag = async () => {
        if (!tagName.trim()) {
            toast({
                title: "Error",
                description: "Tag name is required",
                variant: "destructive"
            });
            return;
        }

        const newTag: TagData = {
            id: Date.now().toString(),
            name: tagName.trim(),
            tenantName: selectedTenant,
            description: tagDescription.trim() || "",
            createdBy: email,
            createdAt: new Date().toLocaleString(),
            updatedBy: email,
            updatedAt: new Date().toLocaleString()
        };

        await createTags({
            name: tagName.trim(),
            tenantName: selectedTenant,
            description: tagDescription.trim() || ""
        });
        fetchTags();
        setTags(prev => [...prev, newTag]);
        setTagName("");
        setTagDescription("");
        setIsCreateDialogOpen(false);

        toast({
            title: "Tag Created",
            description: `Tag "${newTag.name}" has been created successfully.`
        });
    };

    const openEditDialog = (tag: TagData) => {
        setIsEdit(true)
        setSelectedTag(tag);
        setTagName(tag.name);
        setTagDescription(tag.description || "");
        setIsCreateDialogOpen(true)
    }


    const handleEditTag = async () => {

        if (!tagName.trim()) {
            toast({
                title: "Error",
                description: "Tag name is required",
                variant: "destructive"
            });
            return;
        }

        await updateTag(selectedTag?.id || "-1", tagName.trim(), selectedTenant, tagDescription.trim() || "");

        toast({
            title: "Edit Tag",
            description: "Edited successfully"
        });
        const updatedTag = {
            ...selectedTag,
            name: tagName.trim(),
            description: tagDescription.trim() || "",
            tenant: selectedTenant,
        };

        setTags((prev) =>
            prev.map((tag) =>
                tag.id === selectedTag?.id ? { ...tag, ...updatedTag } : tag
            )
        );

        setTagName("");
        setTagDescription("");

        setIsCreateDialogOpen(false);
        setSelectedTag(undefined)
        setIsEdit(false)
    };

    const handleSelectTag = (tag: TagData) => {
        setSelectedTag(tag);
        setShowDeleteConfirmation(true)
    }


    const handleDeleteTag = async (tagId: string, tagName: string) => {
        await deleteTag(tagId);
        setTags(prev => prev.filter(tag => tag.id !== tagId));
        setShowDeleteConfirmation(false);
        setSelectedTag(undefined);
        toast({
            title: "Tag Deleted",
            description: `Tag "${tagName}" has been deleted successfully.`
        });
    };

    useEffect(() => {
        if (!selectedTenant) return;
        fetchTags();
    }, [currentPage, searchQuery, selectedTenant]);

    useEffect(() => {
        writePersistedListState(LIST_STATE_KEYS.tags, {
            currentPage,
            searchQuery,
            selectedTenant,
        });
    }, [currentPage, searchQuery, selectedTenant]);


    return (
        <div className="tags-page">
            <div className="tags-toolbar">
                <TenantLists
                    onSelectorChange={(tenant: string) => {
                        runTenantChangeWithPageReset(() => setSelectedTenant(tenant));
                    }}
                    isLabelNeeded
                    variant="card"
                />
                <div className="tags-search-field">
                    <Label htmlFor="tags-search">
                        <Search aria-hidden="true" /> Search
                    </Label>
                    <div className="tags-search-input">
                        <Search aria-hidden="true" />
                        <Input
                            id="tags-search"
                            placeholder="Search tags by name or creator..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
                <div className="tags-toolbar-actions">
                    <span className="tags-count">
                        {totalElements} {totalElements === 1 ? "tag" : "tags"} found
                    </span>
                    <EndpointActionButton
                        onClick={() => {
                            setIsEdit(false);
                            setIsCreateDialogOpen(true);
                        }}
                        className="tags-create"
                    >
                        <Plus className="w-4 h-4" />
                        Create New Tag
                    </EndpointActionButton>
                </div>
            </div>

            <Card className="tags-panel">
                <CardHeader className="tags-panel-header">
                    <span className="tags-panel-icon">
                        <Tag aria-hidden="true" />
                    </span>
                    <div>
                        <CardTitle>Global tags</CardTitle>
                        <p>Labels for organizing endpoints and resources</p>
                    </div>
                </CardHeader>
                <CardContent className="tags-panel-content">
                    {loading ? (
                        <div className="tags-grid" role="status" aria-label="Loading tags">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="tag-card tag-loading-card" aria-hidden="true">
                                    <div className="tag-card-heading">
                                        <Skeleton className="tag-loading-icon" />
                                        <Skeleton className="tag-loading-title" />
                                        <Skeleton className="tag-loading-badge" />
                                    </div>
                                    <Skeleton className="tag-loading-meta" />
                                    <Skeleton className="tag-loading-meta" />
                                    <Skeleton className="tag-loading-description" />
                                    <div className="tag-card-actions">
                                        <Skeleton className="tag-loading-action" />
                                        <Skeleton className="tag-loading-action" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : tags.length === 0 ? (
                        <EmptyStateCard
                            icon={Tag}
                            title={searchQuery ? "No tags match your search" : "No tags created yet"}
                            description={
                                searchQuery
                                    ? "Try adjusting your search terms or create a new tag."
                                    : "Create your first global tag to organize endpoints and resources."
                            }
                            embedded
                            className="tags-empty"
                            action={
                                !searchQuery ? (
                                    <EndpointActionButton
                                        onClick={() => {
                                            setIsEdit(false);
                                            setIsCreateDialogOpen(true);
                                        }}
                                        className="tags-create"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Create Your First Tag
                                    </EndpointActionButton>
                                ) : undefined
                            }
                        />
                    ) : (
                        <div className="tags-grid">
                            {tags.map((tag) => (
                                <Card key={tag.id} className="tag-card" data-testid={`tag-card-${tag.id}`}>
                                    <CardContent className="tag-card-content">
                                        <div className="tag-card-heading">
                                            <span className="tag-card-icon">
                                                <Tag aria-hidden="true" />
                                            </span>
                                            <h3>{tag.name}</h3>
                                            <span className="tag-scope">Global</span>
                                        </div>
                                        <div className="tag-metadata">
                                            <div>
                                                <UserRound aria-hidden="true" />
                                                <span>{tag.createdBy}</span>
                                            </div>
                                            <div>
                                                <CalendarDays aria-hidden="true" />
                                                <span>{formatDate(tag.createdAt)}</span>
                                            </div>
                                        </div>
                                        <p className="tag-description">
                                            {tag.description || "No description provided"}
                                        </p>
                                        <div className="tag-card-actions">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openEditDialog(tag)}
                                                aria-label={`Edit ${tag.name}`}
                                                disabled={
                                                    !(hasElevatedTagPrivileges || userData?.email === tag.createdBy)
                                                }
                                            >
                                                <Edit aria-hidden="true" /> Edit
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleSelectTag(tag)}
                                                className="tag-delete"
                                                aria-label={`Delete ${tag.name}`}
                                                disabled={
                                                    !(hasElevatedTagPrivileges || userData?.email === tag.createdBy)
                                                }
                                            >
                                                <Trash2 aria-hidden="true" /> Delete
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </CardContent>
                {!loading && tags.length > 0 && totalPages > 1 && (
                    <div className="tags-pagination">
                        <span>
                            Showing {(currentPage - 1) * TAGS_PER_PAGE + 1}–
                            {Math.min(currentPage * TAGS_PER_PAGE, totalElements)} of {totalElements} tags
                        </span>
                        <CardPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            label="Tags pagination"
                        />
                    </div>
                )}
            </Card>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="management-dialog tags-dialog">
                    <DialogHeader className="management-dialog-header">
                        <div className="management-dialog-heading">
                            <span className="management-dialog-icon">
                                <Tag aria-hidden="true" />
                            </span>
                            <div>
                                <DialogTitle>{isEdit ? "Edit Global Tag" : "Create Global Tag"}</DialogTitle>
                                <DialogDescription>
                                    {isEdit
                                        ? "Update the label and description for this tag."
                                        : "Add a label to organize endpoints and resources."}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    <div className="management-dialog-body">
                        <div className="tags-dialog-context">
                            <Building2 aria-hidden="true" />
                            <span>{selectedTenant || "Selected tenant"}</span>
                            <span className="tag-scope">Global tag</span>
                        </div>
                        <div className="management-form-field">
                            <Label htmlFor="tag-name">Tag Name *</Label>
                            <Input
                                id="tag-name"
                                placeholder="Enter tag name (e.g., production)"
                                value={tagName}
                                onChange={(e) => setTagName(e.target.value)}
                            />
                        </div>
                        <div className="management-form-field">
                            <Label htmlFor="tag-description">Description</Label>
                            <Textarea
                                id="tag-description"
                                placeholder="Provide context for this tag..."
                                value={tagDescription}
                                onChange={(e) => setTagDescription(e.target.value)}
                                rows={4}
                            />
                        </div>
                    </div>
                    <DialogFooter className="management-dialog-footer">
                        <Button
                            variant="outline"
                            disabled={isSaving}
                            onClick={() => {
                                setIsCreateDialogOpen(false);
                                setTagName("");
                                setTagDescription("");
                            }}
                        >
                            Cancel
                        </Button>
                        <EndpointActionButton
                            disabled={isSaving}
                            onClick={async () => {
                                setIsSaving(true);
                                try {
                                    if (isEdit) await handleEditTag();
                                    else await handleCreateTag();
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                        >
                            {isSaving && <LoaderCircle className="w-4 h-4 animate-spin" aria-hidden="true" />}
                            {isSaving ? "Saving..." : isEdit ? "Save changes" : "Create Tag"}
                        </EndpointActionButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ConfirmationDialog
                open={showDeleteConfirmation}
                onOpenChange={setShowDeleteConfirmation}
                className="management-dialog management-confirmation"
                title="Delete Tag"
                description={`Are you sure, you want to delete this Tag - ${selectedTag?.name}?`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="destructive"
                onConfirm={() => {
                    if (selectedTag) handleDeleteTag(selectedTag.id, selectedTag.name);
                }}
            />
        </div>
    );
}
